/**
 * URL encoding, local persistence, and a compact Formspace handoff.
 *
 * No quote reference is generated here. A browser-only reference cannot be
 * looked up by Teamward and creates a false promise. The Formspace submission
 * itself is the record for the launch version.
 */

const STORAGE_KEY = 'teamward.quote.v1';

export function encodeQuote({
  headcount,
  perPersonBudget,
  weekend,
  venueId,
  venueIds,
  vendorIds,
  foodIds,
  foodId,
  facilitationId,
  planningSupportId,
  includeServiceCharge,
  dateText,
}) {
  const params = new URLSearchParams();

  if (headcount) {
    params.set('n', String(headcount));
  }

  if (
    perPersonBudget != null
  ) {
    params.set(
      'pp',
      String(perPersonBudget)
    );
  }

  if (weekend) {
    params.set('we', '1');
  }

  const selectedVenueIds = Array.isArray(venueIds) ? venueIds : venueId ? [venueId] : [];
  if (selectedVenueIds.length) {
    params.set('venue', [...new Set(selectedVenueIds)].join(','));
  }

  if (vendorIds && vendorIds.length) {
    params.set(
      'v',
      vendorIds.join(',')
    );
  }

  const selectedFoodIds =
    Array.isArray(foodIds) &&
    foodIds.length
      ? foodIds.filter(Boolean)
      : foodId &&
          foodId !== 'none'
        ? [foodId]
        : [];

  if (selectedFoodIds.length) {
    params.set(
      'food',
      selectedFoodIds.join(',')
    );
  }

  if (
    facilitationId &&
    facilitationId !== 'none'
  ) {
    params.set(
      'fac',
      facilitationId
    );
  }

  if (
    planningSupportId &&
    planningSupportId !== 'none'
  ) {
    params.set(
      'plan',
      planningSupportId
    );
  }

  if (
    includeServiceCharge === false
  ) {
    params.set('svc', '0');
  }

  if (dateText) {
    params.set(
      'when',
      dateText
    );
  }

  return params.toString();
}

export function decodeQuote(search) {
  const params =
    new URLSearchParams(
      search || ''
    );

  const headcount = Number(
    params.get('n')
  );

  const perPersonBudget =
    Number(
      params.get('pp')
    );

  const foodIds =
    params.get('food')
      ? params
          .get('food')
          .split(',')
          .filter(Boolean)
      : null;

  return {
    headcount:
      Number.isFinite(headcount) &&
      headcount > 0
        ? headcount
        : null,

    perPersonBudget:
      Number.isFinite(
        perPersonBudget
      ) &&
      perPersonBudget > 0
        ? perPersonBudget
        : null,

    weekend:
      params.get('we') === '1',

    venueId:
      params.get('venue')?.split(',').filter(Boolean)[0] || null,

    venueIds: params.has('venue') ? [...new Set(params.get('venue').split(',').filter(Boolean))] : null,

    vendorIds:
      params.get('v')
        ? params
            .get('v')
            .split(',')
            .filter(Boolean)
        : null,

    foodIds,

    foodId:
      foodIds && foodIds.length
        ? foodIds[0]
        : null,

    facilitationId:
      params.get('fac') || null,

    planningSupportId:
      params.get('plan') || null,

    includeServiceCharge:
      params.has('svc')
        ? params.get('svc') !== '0'
        : null,

    dateText:
      params.get('when') || null,
  };
}

// -------------------------------------------------------------------------
// Local persistence
// -------------------------------------------------------------------------

export function saveQuote(state) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...state,
        savedAt:
          new Date().toISOString(),
      })
    );

    return true;
  } catch (err) {
    return false;
  }
}

export function loadQuote() {
  try {
    const raw =
      window.localStorage.getItem(
        STORAGE_KEY
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    return parsed &&
      typeof parsed === 'object'
      ? parsed
      : null;
  } catch (err) {
    return null;
  }
}

export function clearQuote() {
  try {
    window.localStorage.removeItem(
      STORAGE_KEY
    );

    return true;
  } catch (err) {
    return false;
  }
}

// -------------------------------------------------------------------------
// Handoff to the booking request form
// -------------------------------------------------------------------------

export const BOOKING_FORM_URL =
  'https://frontal-firestorm-840.forms.space/request-booking-s425cb';

export const PREFILL_FIELDS = {
  experienceName:
    'experienceName',

  experienceLocation:
    'experienceLocation',
};

export function bookingHandoffUrl({
  vendorNames,
  city,
  state,
  headcount,
  totalText,
  planningSupport,
}) {
  const url =
    new URL(BOOKING_FORM_URL);

  const set = (
    field,
    value
  ) => {
    if (
      value == null ||
      value === ''
    ) {
      return;
    }

    url.searchParams.set(
      `prefill.${field}`,
      String(value)
    );
  };

  const compactSummary = [
    vendorNames ||
      'Denver team-event estimate',

    headcount
      ? `${headcount} people`
      : null,

    totalText
      ? `Estimated ${totalText}`
      : null,

    planningSupport
      ? `Support: ${planningSupport}`
      : null,
  ]
    .filter(Boolean)
    .join(' | ')
    .slice(0, 500);

  set(
    PREFILL_FIELDS.experienceName,
    compactSummary
  );

  set(
    PREFILL_FIELDS.experienceLocation,
    city && state
      ? `${city}, ${state}`
      : city
  );

  return url.toString();
}

// Opens the booking request form with the builder's plan and the guest's
// contact details filled in. The form is protected by a bot check, so the
// guest still presses "Complete" on Formspace to send it.
export const PLAN_REQUEST_FIELDS = {
  experienceName: 'experienceName',
  experienceLocation: 'experienceLocation',
  name: 'yourName',
  email: 'workEmail',
  company: 'companyName',
  groupSize: 'groupSize',
  eventType: 'eventType',
  preferredDate: 'preferredDate',
  budgetRange: 'budgetRange',
  details: 'additionalDetails',
};

export function planRequestUrl({
  name,
  email,
  company,
  date,
  headcount,
  city,
  state,
  summary,
  budgetText,
  details,
}) {
  const url = new URL(BOOKING_FORM_URL);

  const set = (field, value, max = 500) => {
    if (value == null || value === '') return;
    url.searchParams.set(`prefill.${field}`, String(value).slice(0, max));
  };

  set(PLAN_REQUEST_FIELDS.experienceName, summary || 'Denver team-event package');
  set(PLAN_REQUEST_FIELDS.experienceLocation, city && state ? `${city}, ${state}` : city);
  set(PLAN_REQUEST_FIELDS.name, name && name.trim(), 120);
  set(PLAN_REQUEST_FIELDS.email, email && email.trim(), 160);
  set(PLAN_REQUEST_FIELDS.company, company && company.trim(), 160);
  set(PLAN_REQUEST_FIELDS.groupSize, headcount);
  set(PLAN_REQUEST_FIELDS.eventType, 'Team event package (budget builder)');
  set(PLAN_REQUEST_FIELDS.preferredDate, /^\d{4}-\d{2}-\d{2}$/.test(date || '') ? date : null);
  set(PLAN_REQUEST_FIELDS.budgetRange, budgetText);
  set(PLAN_REQUEST_FIELDS.details, details, 3500);

  return url.toString();
}
